import React, { useEffect, useState } from "react";

import {
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  styled,
  Typography
} from '@mui/material';
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { NftStats, Trait, TraitValue } from "../../types/nft";
import { orderBy, sum } from "lodash";
import useResizeObserver from "use-resize-observer";
import { areEqual } from 'react-window';


import { Rarity } from '../../types/nft';

import { actions as filterAction } from '../../services/filter';
import { calculatePossibilites } from '../../utils/calculatePossibilites';

const Root = styled('div')({
  // height: '100%',
  overflow: 'scroll',
  maxWidth: 250,
  flex: 1,
});


interface FilterItemProps {
  count: number;
  title: string;
  secondaryTitle?: string;
  checked?: boolean;
  trait?: string;
  value?: string;
  onClick?: () => void;
}

const _FilterItem: React.FC<FilterItemProps> = ({ count, title, secondaryTitle, trait, value, checked, onClick }) => {
  const dispatch = useAppDispatch();

  const toggleFilter = (key: string, value: string) => () => {
    if (checked) {
      return;
    }
    dispatch(filterAction.setTraitFilter({
      [ key ]: value
    }))
  }

  return (
    <ListItem
      secondaryAction={
        <Typography variant="caption">{count}</Typography>
      }
      disablePadding
    >
      <ListItemButton role={undefined} dense sx={{ borderRadius: 3 }} onClick={onClick ?? toggleFilter(trait ?? '', value ?? '')}>
        <ListItemIcon sx={{ minWidth: 'auto' }}>
          <Checkbox
            edge="start"
            disableRipple
            tabIndex={-1}
            size="medium"
            checked={Boolean(checked)}
          />
        </ListItemIcon>
        <ListItemText primary={title} secondary={secondaryTitle} />
      </ListItemButton>
    </ListItem>
  )
}

const FilterItem = React.memo(_FilterItem, areEqual);

interface TraitFilterProps {
  stats: NftStats,
  trait: Trait,
  values: TraitValue[]
  filter?: string

}
const _TraitFilters: React.FC<TraitFilterProps> = ({ trait, values, stats, filter }) => {

  const total = sum(Object.values(stats[ trait.name ]));
  const allChecked = (filter === 'All' || typeof filter === 'undefined');

  return (
    <React.Fragment>
      <List
        key={trait.name}
        subheader={
          <ListSubheader>
            {trait.name} {trait.possibility}%
          </ListSubheader>
        }
      >
        <FilterItem
          title={'All'}
          count={total}
          checked={allChecked}
          trait={trait.name}
          value={'All'}
        />
        {
          values.map((value, index) => {
            const count = stats[ trait.name ][ value.name ];
            const possibilities = calculatePossibilites(trait);
            return (
              <FilterItem
                key={index}
                title={value.name}
                secondaryTitle={`${possibilities[ value.possibility ]}%`}
                count={count}
                checked={filter === value.name}
                trait={trait.name}
                value={value.name}
              />
            )
          })
        }
      </List>
    </React.Fragment>
  )
}

const TraitFilters = React.memo(_TraitFilters, areEqual);



const Filters: React.FC = () => {
  const dispatch = useAppDispatch();

  const stats = useAppSelector(state => state.stats);
  const traits = useAppSelector(state => state.traits);
  const filters = useAppSelector(state => state.filter);

  const [ traitsList, setTraitsList ] = useState<Trait[]>([]);
  const { ref, width = 0, height = 0 } = useResizeObserver<HTMLDivElement>();

  useEffect(() => {
    const list = orderBy(traits.traits, 'name');
    setTraitsList(list);
  }, [ traits.traits ]);


  const toggleRarityFilter = (rarity: string) => () => {
    if (filters.rarity === rarity) {
      return;
    }
    dispatch(filterAction.setRarityFilter(rarity));
  }


  const loading = stats.loading || traits.loading;

  return (
    <Root ref={ref} style={{ maxHeight: height ? height : '100%', width }}>
      {
        loading ? null : (
          <React.Fragment>
            <List
              subheader={
                <ListSubheader>
                  Rarity
                </ListSubheader>
              }
            >
              <FilterItem
                title={'All'}
                count={sum(Object.values(stats.rarity))}
                onClick={toggleRarityFilter('All')}
                checked={filters.rarity === 'All'}
              />
              <FilterItem
                title={Rarity.Common}
                count={stats.rarity.Common}
                onClick={toggleRarityFilter(Rarity.Common)}
                checked={filters.rarity === Rarity.Common}
              />
              <FilterItem
                title={Rarity.Uncommon}
                count={stats.rarity.Uncommon}
                onClick={toggleRarityFilter(Rarity.Uncommon)}
                checked={filters.rarity === Rarity.Uncommon}
              />
              <FilterItem
                title={Rarity.Rare}
                count={stats.rarity.Rare}
                onClick={toggleRarityFilter(Rarity.Rare)}
                checked={filters.rarity === Rarity.Rare}
              />
              <FilterItem
                title={Rarity.Epic}
                count={stats.rarity.Epic}
                onClick={toggleRarityFilter(Rarity.Epic)}
                checked={filters.rarity === Rarity.Epic}
              />
              <FilterItem
                title={Rarity.Legendary}
                count={stats.rarity.Legendary}
                onClick={toggleRarityFilter(Rarity.Legendary)}
                checked={filters.rarity === Rarity.Legendary}
              />
            </List>
            {
              traitsList.map(trait => {
                return (
                  <TraitFilters
                    key={trait.name}
                    trait={trait}
                    values={trait.values}
                    filter={filters?.traits?.[ trait.name ]}
                    stats={stats.traits}
                  />
                )
                // return (
                //   <List
                //     key={trait.name}
                //     subheader={
                //       <ListSubheader>
                //         {trait.name} {trait.possibility}%
                //       </ListSubheader>
                //     }
                //   >
                //     {
                //       trait.values.map((value, index) => {
                //         const count = stats.traits[ trait.name ][ value.name ];
                //         return (
                //           <FilterItem
                //             key={index}
                //             title={value.name}
                //             secondaryTitle={`${value.possibility}%`}
                //             count={count}
                //             checked={Boolean(filters?.[ trait.name ]?.[ value.name ])}
                //             onClick={toggleFilter(trait.name, value.name)}
                //           />
                //         )
                //       })
                //     }
                //   </List>
                // )
              })
            }
          </React.Fragment>
        )
      }

    </Root>
  );
};

export default Filters;